import { Helmet } from "react-helmet-async";
import { Link, useParams } from "wouter";
import { ArrowLeft, Clock, ChevronDown, ArrowRight } from "lucide-react";
import { useState } from "react";
import { getBlogPostBySlug, type BlogSection } from "@/data/blog-posts";

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden">
      <button
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-semibold text-white text-sm md:text-base">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-6 pb-5 text-slate-300 text-sm leading-relaxed">{a}</div>
      )}
    </div>
  );
}

function renderSection(section: BlogSection, index: number) {
  switch (section.type) {
    case "h2":
      return (
        <h2 key={index} className="text-2xl font-bold text-white mt-10 mb-4">
          {section.text}
        </h2>
      );
    case "p":
      return (
        <p key={index} className="text-slate-300 leading-relaxed mb-5">
          {section.text}
        </p>
      );
    case "ul":
      return (
        <ul key={index} className="space-y-4 mb-6">
          {section.items?.map((item, i) => (
            <li key={i} className="flex gap-3 text-slate-300 text-sm leading-relaxed">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0"></span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "faq":
      return (
        <div key={index} className="space-y-3 mt-6">
          {section.faqs?.map(({ q, a }) => (
            <FAQItem key={q} q={q} a={a} />
          ))}
        </div>
      );
    case "cta":
      return (
        <div key={index} className="my-8 p-5 rounded-xl border border-cyan-500/15 bg-cyan-500/5">
          <p className="text-sm font-semibold text-cyan-400 mb-3">Explore ScribPost</p>
          <div className="flex flex-wrap gap-3">
            {section.links?.map(({ text, href }) => (
              <Link
                key={href}
                href={href}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-white/10 hover:bg-white/15 border border-white/10 rounded-lg px-4 py-2 transition-colors"
              >
                {text}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPostPage() {
  const params = useParams<{ slug: string }>();
  const post = getBlogPostBySlug(params.slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0D1A2A] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Post not found</h1>
          <Link href="/blog" className="text-cyan-400 hover:text-cyan-300">← Back to blog</Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.datePublished,
    dateModified: post.datePublished,
    author: {
      "@type": "Person",
      name: post.author,
    },
    publisher: {
      "@type": "Organization",
      name: "ScribPost",
      url: "https://scribpost.com",
    },
    url: `https://scribpost.com/blog/${post.slug}`,
  };

  const faqSection = post.content.find((s) => s.type === "faq");
  const faqJsonLd = faqSection?.faqs
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqSection.faqs.map(({ q, a }) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      }
    : null;

  return (
    <>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={`https://scribpost.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://scribpost.com/blog/${post.slug}`} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        {faqJsonLd && (
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        )}
      </Helmet>

      <div className="min-h-screen bg-[#0D1A2A] text-white">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0D1A2A]/90 backdrop-blur-md border-b border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-2 font-bold text-xl">
              <span className="text-cyan-400">Scrib</span><span className="text-white">Post</span>
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm text-slate-300">
              <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link href="/about" className="hover:text-white transition-colors">About</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/login" className="text-sm text-slate-300 hover:text-white transition-colors hidden sm:block">
                Log in
              </Link>
              <Link
                href="/start"
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity text-white"
              >
                Start Free
              </Link>
            </div>
          </div>
        </nav>

        <div className="pt-28 pb-20 px-4 sm:px-6">
          <div className="max-w-2xl mx-auto">
            {/* Back */}
            <Link href="/blog" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to blog
            </Link>

            {/* Meta */}
            <div className="flex items-center gap-3 mb-5 text-xs text-slate-500">
              <span>{post.date}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {post.readTime}
              </span>
              <span>·</span>
              <span>{post.author}</span>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8 leading-tight">
              {post.title}
            </h1>

            {/* Content */}
            <article>
              {post.content.map((section, i) => renderSection(section, i))}
            </article>

            {/* Internal CTA */}
            <div className="mt-14 p-6 rounded-2xl border border-cyan-500/20 bg-cyan-500/5">
              <h3 className="font-bold text-white mb-2">Ready to write in your voice?</h3>
              <p className="text-slate-300 text-sm mb-4 leading-relaxed">
                ScribPost trains on your writing samples and generates LinkedIn posts, tweets, and more in your exact voice. Start free — no credit card needed.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity text-white flex items-center justify-center gap-2"
                >
                  Try ScribPost Free <ArrowLeft className="w-4 h-4 rotate-180" />
                </Link>
                <Link
                  href="/pricing"
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-white/20 hover:bg-white/5 transition-colors text-white flex items-center justify-center"
                >
                  See pricing
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-white/10 py-10 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <Link href="/" className="flex items-center gap-2 font-bold text-lg">
                <span className="text-cyan-400">Scrib</span><span className="text-white">Post</span>
              </Link>
              <nav className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
                <Link href="/about" className="hover:text-white transition-colors">About</Link>
                <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
              </nav>
              <p className="text-xs text-slate-600">© 2026 ScribPost. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
